// src/components/staff/mobile/MobileStaffSchedule.jsx
// Mobile schedule view — mirrors EventDetailsPanel + EventActionModal logic
// Visible to all staff (20+), actions gated by hasMinLevel(75)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Clock, Plane, User, CheckCircle, XCircle,
  DollarSign, FileText, AlertCircle, Fuel, ChevronDown,
  ChevronUp, History, RefreshCw, X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

// ─── Constants ────────────────────────────────────────────────────────────────
const EVENT_TYPES = {
  flight:      { label: 'Flight',      color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  fuel:        { label: 'Fuel',        color: '#fb923c', bg: 'rgba(249,115,22,0.15)' },
  maintenance: { label: 'Maintenance', color: '#f87171', bg: 'rgba(239,68,68,0.15)'  },
  meeting:     { label: 'Meeting',     color: '#a78bfa', bg: 'rgba(167,139,250,0.15)'},
  appointment: { label: 'Appointment', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
};

const STATUS_STEPS = [
  { key: 'check_in',  label: 'Check-in',  Icon: CheckCircle, color: '#22c55e', field: 'check_in',        value: true    },
  { key: 'check_out', label: 'Check-out', Icon: XCircle,     color: '#ef4444', field: 'check_out',       value: true    },
  { key: 'billing',   label: 'Billing',   Icon: DollarSign,  color: '#eab308', field: 'billing_status',  value: 'paid'  },
  { key: 'grading',   label: 'Grading',   Icon: FileText,    color: '#3b82f6', field: 'grading',         value: null    },
  { key: 'squawk',    label: 'Squawk',    Icon: AlertCircle, color: '#fb923c', field: 'squawk',          value: null    },
  { key: 'fuel',      label: 'Fuel',      Icon: Fuel,        color: '#22d3ee', field: 'fuel_reimbursed', value: true    },
];

const isCompleted = (event, key) => {
  switch (key) {
    case 'check_in':  return !!event.check_in;
    case 'check_out': return !!event.check_out;
    case 'billing':   return event.billing_status === 'paid';
    case 'grading':   return !!event.grading;
    case 'squawk':    return !!event.squawk;
    case 'fuel':      return !!event.fuel_reimbursed;
    default:          return false;
  }
};

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 12, padding: '12px 14px', color: '#f1f5f9', fontSize: 15,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif',
};

// ─── Action Bottom Sheet ──────────────────────────────────────────────────────
const ActionSheet = ({ open, step, event, onClose, onSaved }) => {
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open || !step) return null;

  const handleSave = async () => {
    if (!comment.trim()) { setError('A comment is required'); return; }
    setSaving(true); setError('');
    try {
      const fieldValue = (step.key === 'grading' || step.key === 'squawk') ? comment.trim() : step.value;
      await axios.post(`${API_BASE}/schedule/events/${event.id}/audit`, {
        action: step.key, comment: comment.trim(),
        old_value: null, new_value: fieldValue !== true ? fieldValue : 'completed',
      }, { headers: authHeaders() });
      await axios.put(`${API_BASE}/schedule/events/${event.id}`, { [step.field]: fieldValue }, { headers: authHeaders() });
      onSaved();
      onClose();
      setComment('');
    } catch (err) {
      setError(err.response?.data?.detail ?? 'Error saving action');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#1e293b', borderRadius: '24px 24px 0 0', maxHeight: '80dvh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)', animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)' }}>
        <style>{`@keyframes slideUp { from { transform:translateY(100%) } to { transform:translateY(0) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#334155' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <step.Icon size={16} color={step.color} />
            <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>Mark {step.label}</span>
          </div>
          <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={15} color="#94a3b8" />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '0 20px 24px', flex: 1 }}>
          <div style={{ background: '#0f172a', borderRadius: 12, padding: '10px 13px', marginBottom: 14 }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>{event?.title}</span>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Comment *</label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder={step.key === 'grading' ? 'Grade and service notes…' : step.key === 'squawk' ? 'Squawk code and issue description…' : 'Add notes for this action…'} style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} />
            <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', marginTop: 3 }}>{comment.length}/500</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: '#0f172a', borderRadius: 12, marginBottom: error ? 12 : 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: 9, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#60a5fa' }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <span style={{ fontSize: 12, color: '#64748b' }}>By {user?.first_name} {user?.last_name}</span>
          </div>
          {error && (
            <div style={{ padding: '10px 13px', background: 'rgba(239,68,68,0.1)', border: '0.5px solid rgba(239,68,68,0.3)', borderRadius: 12, marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#fca5a5' }}>{error}</span>
            </div>
          )}
          <button onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '15px', borderRadius: 14, background: saving ? '#166534' : '#22c55e', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <CheckCircle size={17} />}
            {saving ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Event Card ───────────────────────────────────────────────────────────────
const EventCard = ({ event, canManage, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [actionStep, setActionStep] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const et = EVENT_TYPES[event.event_type] ?? EVENT_TYPES.appointment;
  const progress = event.progress ?? 0;
  const start = new Date(event.start_time);
  const end = new Date(event.end_time);

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/schedule/events/${event.id}/audit`, { headers: authHeaders() });
      setAuditLogs(res.data ?? []);
    } catch (e) { console.error(e); }
    finally { setLogsLoading(false); }
  };

  const handleShowHistory = () => {
    setShowHistory(s => !s);
    if (!showHistory && auditLogs.length === 0) loadLogs();
  };

  return (
    <>
      <div style={{ background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155', overflow: 'hidden' }}>
        {/* Color strip */}
        <div style={{ height: 3, background: et.color }} />

        {/* Header row */}
        <button onClick={() => setExpanded(e => !e)} style={{ width: '100%', background: 'none', border: 'none', padding: '13px 15px', textAlign: 'left', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: et.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Calendar size={17} color={et.color} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{event.title}</span>
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: et.bg, color: et.color, fontWeight: 600, flexShrink: 0 }}>{et.label}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Clock size={11} color="#475569" />
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {/* Mini progress */}
              <div style={{ marginTop: 6, height: 3, background: '#0f172a', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: '#22c55e', borderRadius: 2, transition: 'width 0.3s' }} />
              </div>
            </div>
            {expanded ? <ChevronUp size={15} color="#475569" /> : <ChevronDown size={15} color="#475569" />}
          </div>
        </button>

        {/* Expanded */}
        {expanded && (
          <div style={{ borderTop: '0.5px solid #334155', padding: '13px 15px' }}>
            {/* Meta */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {event.tail_number && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Plane size={13} color="#475569" />
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{event.tail_number}</span>
                </div>
              )}
              {event.staff_first_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <User size={13} color="#475569" />
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{event.staff_first_name} {event.staff_last_name}</span>
                </div>
              )}
              {event.description && (
                <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginTop: 2 }}>{event.description}</p>
              )}
            </div>

            {/* Overall progress bar */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Overall progress</span>
                <span style={{ fontSize: 10, color: '#64748b' }}>{Math.round(progress)}%</span>
              </div>
              <div style={{ height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: '#22c55e', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
            </div>

            {/* Status steps */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Steps</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {STATUS_STEPS.map(step => {
                  const done = isCompleted(event, step.key);
                  return (
                    <div key={step.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <step.Icon size={15} color={done ? step.color : '#334155'} />
                        <span style={{ fontSize: 13, color: done ? step.color : '#475569' }}>{step.label}</span>
                      </div>
                      {done ? (
                        <span style={{ fontSize: 10, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <CheckCircle size={11} color="#22c55e" /> Done
                        </span>
                      ) : canManage ? (
                        <button onClick={() => setActionStep(step)} style={{ padding: '5px 11px', borderRadius: 10, background: '#0f172a', border: '0.5px solid #334155', color: '#94a3b8', fontSize: 11, cursor: 'pointer' }}>
                          Mark
                        </button>
                      ) : (
                        <span style={{ fontSize: 10, color: '#334155' }}>Pending</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* History toggle */}
            <button onClick={handleShowHistory} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: showHistory ? '#3b82f6' : '#475569', fontSize: 12, padding: '6px 0', marginBottom: showHistory ? 10 : 0 }}>
              <History size={14} />
              {showHistory ? 'Hide history' : 'Show action history'}
            </button>

            {showHistory && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                {logsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                    <div style={{ width: 18, height: 18, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '10px 0' }}>No actions recorded</p>
                ) : auditLogs.map(log => (
                  <div key={log.id} style={{ background: '#0f172a', borderRadius: 10, padding: '9px 11px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>{log.action}</span>
                      <span style={{ fontSize: 10, color: '#475569' }}>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    <p style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{log.comment}</p>
                    <span style={{ fontSize: 10, color: '#475569' }}>by {log.first_name} {log.last_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ActionSheet open={!!actionStep} step={actionStep} event={event} onClose={() => setActionStep(null)} onSaved={() => { setActionStep(null); onRefresh(); if (showHistory) loadLogs(); }} />
    </>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffSchedule = () => {
  const { hasMinLevel } = useAuth();
  const canManage = hasMinLevel(75);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/schedule/events`, { headers: authHeaders(), params: { date: selectedDate } });
      setEvents(res.data ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedDate]);

  useEffect(() => { load(); }, [load]);

  const EVENT_TYPE_KEYS = ['all', 'flight', 'fuel', 'maintenance', 'meeting', 'appointment'];
  const filtered = filter === 'all' ? events : events.filter(e => e.event_type === filter);

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Date picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <input
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 14, padding: '11px 14px', color: '#f1f5f9', fontSize: 15, outline: 'none' }}
        />
        <button onClick={load} style={{ background: '#1e293b', border: '0.5px solid #334155', borderRadius: 12, padding: '11px 12px', cursor: 'pointer' }}>
          <RefreshCw size={16} color="#94a3b8" />
        </button>
      </div>

      {/* Type filter */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 14 }}>
        {EVENT_TYPE_KEYS.map(key => {
          const et = EVENT_TYPES[key];
          const active = filter === key;
          return (
            <button key={key} onClick={() => setFilter(key)} style={{
              flexShrink: 0, padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 400,
              background: active ? (et ? et.bg : 'rgba(59,130,246,0.15)') : '#1e293b',
              color: active ? (et ? et.color : '#3b82f6') : '#64748b',
            }}>
              {key === 'all' ? 'All' : (et?.label ?? key)}
            </button>
          );
        })}
      </div>

      {/* Summary */}
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
        {filtered.length} event{filtered.length !== 1 ? 's' : ''} · {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <Calendar size={36} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569' }}>No events for this day</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(ev => (
            <EventCard key={ev.id} event={ev} canManage={canManage} onRefresh={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileStaffSchedule;